import { useMutation, useQuery } from '@tanstack/react-query';

import { QueryKey } from '../../enums/QueryKey';
import {
    getApiKeys,
    getApiPermissions,
    createApiKey,
    updateApiKey,
    deleteApiKey,
    generateApiToken,
} from '../../services/apiKeys';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { useDispatchSuccessfullySavedRecord } from '../useDispatchSuccessfullySavedRecord';
import { queryClient } from '../../services/queryClient';
import { useDeleteRecord, useUpdateRecord } from './crudOperations';
import {
    ApiKeyCredentials,
    ApiKeyForm,
    ApiKeyInterface,
    ApiScopeInterface,
    ApiTokenInterface,
} from '../../types/ApiKey.interface';

export function useApiKeys() {
    return useQuery({
        queryKey: [QueryKey.ApiKeys],
        queryFn: getApiKeys,
        initialData: [],
        refetchOnWindowFocus: false,
    });
}

export function useCreateApiKey({ onSettled }: UseMutationOptions<ApiKeyInterface, Error, ApiKeyForm>) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<ApiKeyInterface, Error, ApiKeyForm>({
        mutationFn: createApiKey,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.ApiKeys]);
            dispatchSuccessfullySavedRecord('ApiKey');
        },
        onError: (error: Error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled: onSettled,
    });
}

export function useUpdateApiKey({ onSettled }: UseMutationOptions<ApiKeyInterface, Error, ApiKeyForm>) {
    return useUpdateRecord<ApiKeyInterface, Error, ApiKeyForm>({
        updateRecordFn: updateApiKey,
        fetchRecordsQueryKey: [QueryKey.ApiKeys],
        recordName: 'API Key',
        onSettled,
    });
}

export function useDeleteApiKey({ onSettled }: UseMutationOptions<ApiKeyInterface, Error, string>) {
    return useDeleteRecord<ApiKeyInterface, Error, string>({
        deleteRecordFn: deleteApiKey,
        fetchRecordsQueryKey: [QueryKey.ApiKeys],
        recordName: 'API Key',
        onSettled,
    });
}

export function useGenerateApiToken({ onSettled }: UseMutationOptions<ApiTokenInterface, Error, ApiKeyCredentials>) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    return useMutation<ApiTokenInterface, Error, ApiKeyCredentials>({
        mutationFn: generateApiToken,
        onError: (error: Error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled,
    });
}

export function useApiPermissions() {
    return useQuery<ApiScopeInterface[], Error, never>({
        queryKey: [QueryKey.ApiPermissions],
        queryFn: getApiPermissions,
        initialData: [],
        refetchOnWindowFocus: false,
    });
}
